import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router';
import { AvatarTypes } from '@chewy/pet-profile-pet-avatar';
import { KibButtonNew } from '@chewy/kib-controls-react';
import classNames from 'classnames';
import { KibCloseIcon, KibChevronLeftIcon } from '@chewy/kib-icons-react';
import { KibSpinningLoader } from '@chewy/kib-overlays-react';
import { getPetWithOwnerName } from 'api/petApi';
import { PetInterface } from 'types/pet';
import { ManagePetLanding } from 'components/manage-pet/ManagePetLanding/ManagePetLanding';
import { ManagePetReason } from 'components/manage-pet/ManagePetReason/ManagePetReason';
import { ManagePetSympathy } from 'components/manage-pet/ManagePetSympathy/ManagePetSympathy';
import { ManagePetErrorPage } from 'components/manage-pet/ManagePetErrorPage/ManagePetErrorPage';
import { ManagePetUndoConfirmation } from 'components/manage-pet/ManagePetUndoConfirmation/ManagePetUndoConfirmation';
import AnalyticsContext from '../context/AnalyticsContext';
import {
  getPetProfilePath,
  getPetsPath,
  getPetEditPath,
  ACCOUNT_OVERVIEW_PATH,
} from '../util/config';
import FetchError from '../types/http';
import useLocationQuery from '../hooks/useLocationQuery';
import { retryCount } from '../util/reactQueryOptions';
import {
  ApiPetProfileStatusEnum,
  ApiPetProfileStatusReasonEnum,
} from '../api/generated-client/PetProfile-Service-A.v1/models';
import './manage-pet.scss';

export default function ManagePet() {
  const goBackCopy = 'Go Back';
  const closeCopy = 'Close';

  let query = useLocationQuery();
  const analytics = useContext(AnalyticsContext);
  const redirectUrl = query.get('redirectUrl');
  const innerRedirectUrl = query.get('innerRedirectUrl');
  const location = useLocation();
  const history = useHistory();

  const [statusChoice, setStatusChoice] = useState<ApiPetProfileStatusEnum | undefined>();
  const [reasonChoice, setReasonChoice] = useState<ApiPetProfileStatusReasonEnum | undefined>();
  const [showError, setShowError] = useState<boolean>(false);
  const [showUndoConfirmation, setShowUndoConfirmation] = useState<boolean>(false);
  const [checkedForRedirect, setCheckedForRedirect] = useState(false);
  const [isInactivePet, setIsInactivePet] = useState(false);

  const showLanding = statusChoice === undefined && reasonChoice === undefined;
  const showReason = statusChoice !== undefined && reasonChoice === undefined;
  const showSympathy = statusChoice !== undefined && reasonChoice !== undefined;

  const [returnToReason, setReturnToReason] = useState(false);

  const { petId } = useParams<{ petId: string }>();
  const { data: pet, error: petError } = useQuery<PetInterface, FetchError>(
    [getPetWithOwnerName.key, petId],
    getPetWithOwnerName,
    {
      retry: retryCount,
    },
  );

  const handleExit = () => {
    if (showUndoConfirmation) {
      redirectUrl
        ? (window.location.href = decodeURIComponent(redirectUrl))
        : (window.location.href = getPetProfilePath(petId));
    } else if (showLanding) {
      innerRedirectUrl && redirectUrl
        ? (window.location.href =
            decodeURIComponent(innerRedirectUrl) +
            `?redirectUrl=${encodeURIComponent(redirectUrl)}`)
        : redirectUrl
        ? (window.location.href = decodeURIComponent(redirectUrl))
        : (window.location.href = getPetEditPath(petId));
    } else if (showReason || showSympathy) {
      !redirectUrl ||
      decodeURIComponent(redirectUrl) === getPetEditPath(petId) ||
      decodeURIComponent(redirectUrl) === getPetProfilePath(petId)
        ? (window.location.href = getPetsPath())
        : (window.location.href = decodeURIComponent(redirectUrl));
    }
  };

  useEffect(() => {
    // NOTE: If pet memorial is deleted right after creation and browser back is clicked, pet object becomes undefined. Redirect to accounts page in this case.
    const inactivePet = pet?.status === ApiPetProfileStatusEnum.Inactive;
    setIsInactivePet(inactivePet);
    if (inactivePet && redirectUrl) {
      decodeURIComponent(redirectUrl) === getPetEditPath(petId)
        ? (window.location.href = getPetsPath())
        : (window.location.href = decodeURIComponent(redirectUrl));
    } else if (inactivePet && !redirectUrl) {
      window.location.href = getPetsPath();
    } else if (!pet) {
      window.location.href = ACCOUNT_OVERVIEW_PATH;
    }
    setCheckedForRedirect(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pet, history]);

  const [headerText, setHeaderText] = useState<string>('');
  const [bodyText1, setBodyText1] = useState<string>('');
  const [bodyText2, setBodyText2] = useState<string | null>();
  const [avatarStroke, setAvatarStroke] = useState<AvatarTypes | null>();

  const showBackButton =
    showSympathy &&
    !returnToReason &&
    !showError &&
    !showUndoConfirmation &&
    statusChoice !== ApiPetProfileStatusEnum.Deleted;

  const handleGoingBack = () => {
    setReturnToReason(true);
    analytics.trackRainbow({
      name: 'pet profile rainbow - clicked',
      category: 'pet-profile-rainbow',
      action: 'click',
      label: 'pet_profile_rainbow_sympathy_back',
    });
  };

  return pet && checkedForRedirect && !isInactivePet ? (
    <div className="manage-pet">
      <div
        className={classNames(`manage-pet__header`, {
          'manage-pet__header--spacing': showBackButton,
        })}
      >
        {showBackButton && (
          <KibButtonNew
            className="manage-pet__go-back"
            data-testid="manage-pet__go-back"
            icon={<KibChevronLeftIcon />}
            iconOnly
            iconPosition="start"
            emphasis="tertiary"
            size="medium"
            onClick={handleGoingBack}
          >
            {goBackCopy}
          </KibButtonNew>
        )}
        <KibButtonNew
          className="manage-pet__close-button"
          data-testid="manage-pet__close-button"
          icon={<KibCloseIcon />}
          iconOnly
          iconPosition="start"
          emphasis="tertiary"
          size="medium"
          onClick={() => {
            if (showLanding) {
              analytics.trackRainbow({
                name: 'pet profile rainbow - clicked',
                category: 'pet-profile-rainbow',
                action: 'click',
                label: 'pet_profile_rainbow_memorialize',
              });
            }
            if (showReason) {
              analytics.trackRainbow({
                name: 'pet profile rainbow - clicked',
                category: 'pet-profile-rainbow',
                action: 'click',
                label: 'pet_profile_rainbow_dismiss',
              });
            }
            handleExit();
          }}
        >
          {closeCopy}
        </KibButtonNew>
      </div>
      <div className="manage-pet-content">
        {showError ? (
          <ManagePetErrorPage pet={pet!} />
        ) : showUndoConfirmation ? (
          <ManagePetUndoConfirmation pet={pet!} />
        ) : showLanding ? (
          <ManagePetLanding
            pet={pet!}
            setStatusChoice={setStatusChoice}
            setShowError={setShowError}
          />
        ) : showReason || returnToReason ? (
          <ManagePetReason
            pet={pet!}
            setShowError={setShowError}
            statusChoice={statusChoice}
            setReasonChoice={setReasonChoice}
            setShowUndoConfirmation={setShowUndoConfirmation}
            setHeaderText={setHeaderText}
            setBodyText1={setBodyText1}
            setBodyText2={setBodyText2}
            setAvatarStroke={setAvatarStroke}
            returnToReason={returnToReason}
            setReturnToReason={setReturnToReason}
          />
        ) : showSympathy && !returnToReason ? (
          <ManagePetSympathy
            pet={pet!}
            headerText={headerText}
            bodyText1={bodyText1}
            bodyText2={bodyText2}
            avatarStroke={avatarStroke}
            statusChoice={statusChoice!}
            reasonChoice={reasonChoice!}
            setShowError={setShowError}
            setShowUndoConfirmation={setShowUndoConfirmation}
          />
        ) : (
          <ManagePetErrorPage pet={pet!} />
        )}
      </div>
    </div>
  ) : (
    <KibSpinningLoader />
  );
}
